import { useCallback, useRef } from 'react';
import { settingsService } from '../../../../services/settingsService';
import {
  type CurrentBrowserDevices,
  DEVICE_LS_KEYS,
  type DeviceApplyKind,
  getStoredJitsiDeviceLabels,
  normalizeDeviceId,
  resolveDeviceByLabel,
} from '../../../../utils/deviceUtils';
import { isDev } from '../../../../utils/optimizedImports';

/**
 * Which label to treat as "the device the user picked" for a slot.
 *
 * Jitsi's persisted `userSelected*Label` wins over `getCurrentDevices()`.
 * `getCurrentDevices()` only reports the device backing the *active* track, so a
 * muted microphone reports an empty `audioInput` and an in-meeting mic change
 * made while muted would never be seen. The persisted selection is written by
 * Jitsi on every device change regardless of mute state, which makes it the
 * signal that actually tracks user intent (SM-1187 reverse sync).
 *
 * The live report is still used as a fallback for the case where Jitsi has an
 * active track but has not persisted a selection (e.g. it fell back to a system
 * default the user never explicitly chose).
 */
const selectedDeviceLabel = (
  persistedLabel: string | undefined,
  liveDevice: unknown,
): { label: string } | null => {
  const live = (liveDevice as { label?: string } | null | undefined)?.label;
  const label = persistedLabel || live;
  return label ? { label } : null;
};

const DEVICES_POLL_INTERVAL_MS = 2000;
const INITIAL_SYNC_DELAY_MS = 10000;
/**
 * Jitsi rewrites the whole `jitsiLocalStorage` blob on any persisted redux
 * change, so the storage signal arrives in bursts. Coalesce them.
 */
const STORAGE_SYNC_DEBOUNCE_MS = 300;

/**
 * The transport event Jitsi emits when it writes back to the page's storage,
 * which is where it records the user's device selection. This is the only
 * notification available: the iframe API exposes no public event for a device
 * *selection* change, and the native `storage` event cannot help either because
 * it does not fire in the window that performed the write — and with
 * `useHostPageLocalStorage: true` the write happens on this very page.
 */
const JITSI_STORAGE_EVENT = 'local-storage-changed';

/** The transport channel all iframe events arrive on; the payload carries `name`. */
const JITSI_TRANSPORT_EVENT = 'event';

export interface DeviceSyncGate {
  getAppliedSlots: () => ReadonlySet<DeviceApplyKind>;
  getSavedDevices: () => CurrentBrowserDevices;
}

const ALL_DEVICE_SLOTS: ReadonlySet<DeviceApplyKind> = new Set([
  'camera',
  'microphone',
  'audioOutput',
]);

/** Reverse-sync a slot only after its saved selection has been applied, or if nothing was saved. */
const canReverseSyncSlot = (
  slot: DeviceApplyKind,
  savedId: string | undefined,
  applied: ReadonlySet<DeviceApplyKind>,
): boolean => !savedId || applied.has(slot);

interface JitsiDeviceApi {
  getCurrentDevices?: () => Promise<{
    videoInput?: unknown;
    audioInput?: unknown;
    audioOutput?: unknown;
  }>;
  /**
   * Private field of `JitsiMeetExternalAPI`. Assigned synchronously in its
   * constructor, so it is available as soon as `new JitsiMeetExternalAPI()`
   * returns — no need to wait for the iframe to load. Treated as optional
   * because it is not part of the public API and a JaaS release could drop it.
   */
  _transport?: {
    on?: (name: string, listener: (payload: { name?: string }) => unknown) => unknown;
    removeListener?: (name: string, listener: (payload: { name?: string }) => unknown) => unknown;
  };
}

export const useJitsiDeviceSync = () => {
  const devicesPollIntervalRef = useRef<number | null>(null);
  const initialSyncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const storageSyncDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const detachTransportRef = useRef<(() => void) | null>(null);
  const gateRef = useRef<DeviceSyncGate | null>(null);

  /**
   * Syncs current devices from Jitsi API to app localStorage and settings.
   * Resolves device IDs by matching labels against enumerated devices.
   */
  const syncCurrentDevicesFromJitsi = useCallback(async (api: JitsiDeviceApi) => {
    if (typeof api.getCurrentDevices !== 'function') {
      if (isDev) {
        console.log('[Sync] getCurrentDevices not available');
      }
      return;
    }

    const lastDevices = {
      camera: localStorage.getItem(DEVICE_LS_KEYS.camera),
      mic: localStorage.getItem(DEVICE_LS_KEYS.microphone),
      spk: localStorage.getItem(DEVICE_LS_KEYS.audioOutput),
    };

    let devices: MediaDeviceInfo[] = [];
    try {
      devices = await navigator.mediaDevices.enumerateDevices();
    } catch (e) {
      if (isDev) {
        console.error('[Sync] enumerateDevices failed:', e);
      }
      return;
    }

    const videoInputs = devices.filter((d) => d.kind === 'videoinput');
    const audioInputs = devices.filter((d) => d.kind === 'audioinput');
    const audioOutputs = devices.filter((d) => d.kind === 'audiooutput');

    let current: { videoInput?: unknown; audioInput?: unknown; audioOutput?: unknown };
    try {
      current = await api.getCurrentDevices();
    } catch (e) {
      console.error('[Sync] getCurrentDevices failed:', e);
      return;
    }

    const changed: Record<string, string> = {};
    const keys = DEVICE_LS_KEYS;
    const jitsiLabels = getStoredJitsiDeviceLabels();
    const gate = gateRef.current;
    const applied = gate?.getAppliedSlots() ?? ALL_DEVICE_SLOTS;
    const saved = gate?.getSavedDevices();

    const camResult = resolveDeviceByLabel(
      videoInputs,
      selectedDeviceLabel(jitsiLabels.camera, current?.videoInput),
      'videoinput',
      lastDevices.camera,
    );
    if (camResult && canReverseSyncSlot('camera', saved?.selectedCamera, applied)) {
      lastDevices.camera = camResult.deviceId;
      localStorage.setItem(keys.camera, camResult.deviceId);
      changed.cameraId = camResult.deviceId;
    }

    const micResult = resolveDeviceByLabel(
      audioInputs,
      selectedDeviceLabel(jitsiLabels.microphone, current?.audioInput),
      'audioinput',
      lastDevices.mic,
    );
    if (micResult && canReverseSyncSlot('microphone', saved?.selectedMicrophone, applied)) {
      lastDevices.mic = micResult.deviceId;
      localStorage.setItem(keys.microphone, micResult.deviceId);
      changed.microphoneId = micResult.deviceId;
    }

    const spkResult = resolveDeviceByLabel(
      audioOutputs,
      selectedDeviceLabel(jitsiLabels.audioOutput, current?.audioOutput),
      'audiooutput',
      lastDevices.spk,
    );
    if (spkResult && canReverseSyncSlot('audioOutput', saved?.selectedAudioOutput, applied)) {
      lastDevices.spk = spkResult.deviceId;
      localStorage.setItem(keys.audioOutput, spkResult.deviceId);
      changed.audioOutputId = spkResult.deviceId;
    }

    const cam = lastDevices.camera || normalizeDeviceId(current?.videoInput);
    const mic = lastDevices.mic || normalizeDeviceId(current?.audioInput);
    const spk = lastDevices.spk || normalizeDeviceId(current?.audioOutput);

    if (Object.keys(changed).length === 0) {
      return;
    }

    if (isDev) {
      console.log('[Sync] in-meeting device change captured', changed);
    }

    const currentSettings = settingsService.getSettings();
    settingsService.updateSettings(
      {
        devices: {
          ...currentSettings.devices,
          ...(cam && { selectedCamera: cam }),
          ...(mic && { selectedMicrophone: mic }),
          ...(spk && { selectedAudioOutput: spk }),
        },
      },
      { validate: true, persist: true },
    );

    window.dispatchEvent(
      new CustomEvent('settings-devices-changed', {
        detail: { source: 'jitsi', ...changed },
      }),
    );
  }, []);

  const runSync = useCallback(
    (api: JitsiDeviceApi) => {
      syncCurrentDevicesFromJitsi(api).catch((e) => {
        if (isDev) {
          console.error('[Sync] device sync failed:', e);
        }
      });
    },
    [syncCurrentDevicesFromJitsi],
  );

  /**
   * Subscribe to Jitsi's storage write-back. Returns whether it attached.
   *
   * Jitsi's own handler for this event is registered in the API constructor and
   * so runs first, meaning the storage it describes is already written by the
   * time we are called — we can just re-read it. Its handler returning `true`
   * does not suppress ours: the transport invokes every listener and only
   * accumulates the return values.
   */
  const attachStorageListener = useCallback(
    (api: JitsiDeviceApi): boolean => {
      // Attaching twice would leak the first listener — only one detach closure
      // is kept — and run every sync twice per event. Mirrors the poll's guard.
      if (detachTransportRef.current) {
        return true;
      }

      const transport = api._transport;
      if (typeof transport?.on !== 'function') {
        return false;
      }

      const onTransportEvent = (payload: { name?: string }): void => {
        // The transport dispatches with a plain `forEach`, so throwing here
        // would break delivery of this message to any later listener.
        try {
          if (payload?.name !== JITSI_STORAGE_EVENT) {
            return;
          }
          if (storageSyncDebounceRef.current) {
            clearTimeout(storageSyncDebounceRef.current);
          }
          storageSyncDebounceRef.current = setTimeout(() => {
            storageSyncDebounceRef.current = null;
            runSync(api);
          }, STORAGE_SYNC_DEBOUNCE_MS);
        } catch (e) {
          if (isDev) {
            console.error('[Sync] storage listener error:', e);
          }
        }
      };

      try {
        transport.on(JITSI_TRANSPORT_EVENT, onTransportEvent);
      } catch {
        return false;
      }

      detachTransportRef.current = () => {
        transport.removeListener?.(JITSI_TRANSPORT_EVENT, onTransportEvent);
      };
      return true;
    },
    [runSync],
  );

  /**
   * Fallback only. Polling cannot distinguish "the user changed something" from
   * "nothing happened", so it both lags the change and burns work every tick;
   * it exists solely for the case where the storage listener cannot attach.
   */
  const startDevicePolling = useCallback(
    (api: JitsiDeviceApi) => {
      if (devicesPollIntervalRef.current !== null) {
        return;
      }
      devicesPollIntervalRef.current = window.setInterval(
        () => runSync(api),
        DEVICES_POLL_INTERVAL_MS,
      );
      initialSyncTimeoutRef.current = setTimeout(() => runSync(api), INITIAL_SYNC_DELAY_MS);
    },
    [runSync],
  );

  /**
   * Start syncing in-meeting device changes back into app settings, preferring
   * Jitsi's storage event and falling back to polling only if that is
   * unavailable.
   */
  const startDeviceSync = useCallback(
    (api: JitsiDeviceApi, gate?: DeviceSyncGate) => {
      gateRef.current = gate ?? null;
      if (attachStorageListener(api)) {
        return;
      }
      console.warn(
        '[Sync] Jitsi storage listener unavailable; falling back to polling for in-meeting device changes',
      );
      startDevicePolling(api);
    },
    [attachStorageListener, startDevicePolling],
  );

  /** Tears down whichever sync mechanism started, plus any pending debounce. */
  const cleanup = useCallback(() => {
    if (devicesPollIntervalRef.current) {
      window.clearInterval(devicesPollIntervalRef.current);
      devicesPollIntervalRef.current = null;
    }

    if (initialSyncTimeoutRef.current) {
      clearTimeout(initialSyncTimeoutRef.current);
      initialSyncTimeoutRef.current = null;
    }

    if (storageSyncDebounceRef.current) {
      clearTimeout(storageSyncDebounceRef.current);
      storageSyncDebounceRef.current = null;
    }

    if (detachTransportRef.current) {
      detachTransportRef.current();
      detachTransportRef.current = null;
    }
  }, []);

  return {
    startDeviceSync,
    cleanup,
  };
};
